import { useMemo, useState } from 'react';
import clsx from 'clsx';
import NukaCarousel from 'nuka-carousel';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import Button from 'honeydew/Button';
import { NextIcon, PreviousIcon } from 'honeydew/icons/Carousels';
import useCarouselSlideWidth from 'hooks/useCarouselSlideWidth';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { trackEvent } from 'utils/analyticsUtils';
import { formatCarouselID } from 'utils/shopUtils';

import styles from './carousel.module.css';

const DEFAULT_CHARACTER_LIMIT = 150;

function Carousel({
    carousel,
    children,
    onSeeAllClick,
    onSlideChange,
    restoreScrollPosition,
    scrollOverride,
    slideOverride,
    upSellsCarousel,
}) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { description, featured, linkTitle, linkUrl, name, photoUrlLarge, photoUrlSmall } = carousel;
    const validLink = linkTitle && linkUrl;
    const tabletOrLargerScreen = useMediaQuery({ minWidth: tailwindTheme?.screens?.md });
    const carouselID = formatCarouselID(name);
    const [displayFullDescription, setDisplayFullDescription] = useState(false);

    // custom breakpoints for carousel slides
    const smDesktopScreen = useMediaQuery({ maxWidth: '910px' });
    const mdDesktopScreen = useMediaQuery({ maxWidth: '1110px' });
    const controlsClasses = 'absolute top-20 z-10';
    const showCarouselImage = featured && photoUrlLarge && photoUrlSmall;
    const carouselLength = carousel.items?.length;

    const slidesToShow = useCarouselSlideWidth({ override: slideOverride });
    const slidesToScroll = useCarouselSlideWidth({ override: scrollOverride, scroll: true });
    const displayArrows = slidesToShow < carousel.items?.length;
    const displaySeeAllButton = carousel.items?.length >= 8 && !upSellsCarousel;
    const displayArrowSize = upSellsCarousel ? 30 : 40;

    const getSmallCarouselBannerWidthClasses = () => {
        if (carouselLength === 1) {
            if (smDesktopScreen) return 'col-span-2';
            if (mdDesktopScreen) return 'col-span-3';
            return 'col-span-4';
        }

        if (carouselLength === 2) {
            if (smDesktopScreen) return 'col-span-1';
            if (mdDesktopScreen) return 'col-span-2';
            return 'col-span-3';
        }

        return '';
    };

    const getSmallCarouselWidthClasses = () => {
        if (carouselLength === 1) return 'col-span-1';
        if (carouselLength === 2) return 'col-span-2';
        return '';
    };

    const getCarouselGridWidthClasses = () => {
        if (smDesktopScreen) return 'grid-cols-3';
        if (mdDesktopScreen) return 'grid-cols-4';
        return 'grid-cols-5';
    };

    const carouselBannerWidthClasses = getSmallCarouselBannerWidthClasses();
    const carouselWidthClasses = getSmallCarouselWidthClasses();
    const carouselGridWidthClasses =
        carouselLength <= 2 && !mobile && showCarouselImage && !upSellsCarousel
            ? `wrapper-small grid ${getCarouselGridWidthClasses()}`
            : '';

    const carouselSingleItemWidthClasses = carouselLength === 1 && mobile ? `wrapper-small` : '';

    const descriptionOverDefaultCharacterLimit = description && description.length > DEFAULT_CHARACTER_LIMIT;

    const restoredSlideIndex = useMemo(() => {
        const carouselLocations =
            window.history.state && window.history.state.carouselLocations
                ? window.history.state.carouselLocations
                : {};
        return Object.prototype.hasOwnProperty.call(carouselLocations, name) ? carouselLocations[name] : undefined;
    });

    const trackSlideChange = (slideIndex) => {
        window.history.replaceState(
            {
                ...window.history.state,
                carouselLocations: {
                    ...(window.history.state ? window.history.state.carouselLocations : {}),
                    [name]: slideIndex,
                },
            },
            ''
        );
    };

    const handleLinkClick = () => {
        trackEvent('carousel link click', {
            carouselLinkCTA: linkTitle,
            carouselLinkURL: linkUrl,
        });

        if (window.isNativeApp) {
            window.ReactNativeWebView?.postMessage(
                JSON.stringify({
                    navigate: 'ShopLink',
                    url: linkUrl,
                })
            );
        }
    };

    return (
        <div>
            {showCarouselImage && carouselLength > 2 && (
                <img
                    className="w-full"
                    alt=""
                    height={mobile ? '300' : '500'}
                    width={mobile ? '750' : '1839'}
                    aria-hidden="true"
                    src={tabletOrLargerScreen ? photoUrlLarge : photoUrlSmall}
                />
            )}
            <div
                className={clsx('relative mb-20 if:bg-oat', {
                    'bg-brand-secondary pb-10': upSellsCarousel,
                    'bg-malt py-10': !upSellsCarousel,
                })}
                id={carouselID}
            >
                <div className={clsx(carouselGridWidthClasses, carouselSingleItemWidthClasses)}>
                    <div className={carouselBannerWidthClasses}>
                        {showCarouselImage && carouselLength <= 2 && (
                            <div>
                                <img
                                    className={clsx(
                                        '-mt-10 w-full object-cover',
                                        { 'aspect-[4/1]': carouselLength === 1 },
                                        { 'aspect-[2.35/1]': carouselLength === 2 }
                                    )}
                                    alt=""
                                    height={mobile ? '300' : '500'}
                                    width={mobile ? '750' : '1839'}
                                    aria-hidden="true"
                                    src={tabletOrLargerScreen ? photoUrlLarge : photoUrlSmall}
                                />
                            </div>
                        )}
                        <div className="p-10">
                            <div
                                className={clsx('mb-10 flex justify-between', {
                                    'mr-[120px]': displayArrows && !displaySeeAllButton && !mobile,
                                    'mr-[200px]': displayArrows && displaySeeAllButton && !mobile,
                                })}
                            >
                                {name &&
                                    (upSellsCarousel ? (
                                        <p className="text-body-md -mb-10 font-grotesk-bold">{name}</p>
                                    ) : (
                                        <h2 className="text-body-lg font-grotesk-bold">{name}</h2>
                                    ))}
                                {displaySeeAllButton && (
                                    <button type="button" className="link w-[90px] md:hidden" onClick={onSeeAllClick}>
                                        See All
                                    </button>
                                )}
                            </div>
                            {description && (
                                <p className="text-body-md mb-10">
                                    {displayFullDescription
                                        ? description
                                        : description.substring(0, DEFAULT_CHARACTER_LIMIT)}
                                    {descriptionOverDefaultCharacterLimit && (
                                        <>
                                            {!displayFullDescription && '…'}
                                            &nbsp;
                                            <button
                                                className="text-body-md font-grotesk-bold underline"
                                                type="button"
                                                onClick={() => {
                                                    setDisplayFullDescription(!displayFullDescription);
                                                }}
                                            >
                                                View {displayFullDescription ? 'Less' : 'More'}
                                            </button>
                                        </>
                                    )}
                                </p>
                            )}
                            {validLink && (
                                <Button
                                    {...(!window.isNativeApp && { as: 'a' })}
                                    className="link text-body-sm text-left"
                                    href={linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="md"
                                    onClick={handleLinkClick}
                                >
                                    {linkTitle}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={clsx(styles.wrapper, carouselWidthClasses)}>
                        <NukaCarousel
                            scrollMode="remainder"
                            className={styles.slider}
                            withoutControls={!tabletOrLargerScreen}
                            renderCenterLeftControls={({ previousDisabled, previousSlide }) => (
                                <div className={`right-70 flex ${!upSellsCarousel && controlsClasses}`}>
                                    {displaySeeAllButton && (
                                        <button type="button" className="link mr-20 w-[90px]" onClick={onSeeAllClick}>
                                            See All
                                        </button>
                                    )}
                                    {displayArrows && (
                                        <button
                                            className={clsx([
                                                {
                                                    'cursor-not-allowed': previousDisabled,
                                                    'absolute left-10 top-[55%] z-10': upSellsCarousel,
                                                },
                                            ])}
                                            type="button"
                                            onClick={previousSlide}
                                        >
                                            <PreviousIcon width={displayArrowSize} disabled={previousDisabled} />
                                        </button>
                                    )}
                                </div>
                            )}
                            renderCenterRightControls={({ nextDisabled, nextSlide }) =>
                                displayArrows && (
                                    <button
                                        className={clsx([
                                            `right-10 ${controlsClasses}`,
                                            { 'cursor-not-allowed': nextDisabled, 'top-[55%]': upSellsCarousel },
                                        ])}
                                        type="button"
                                        onClick={nextSlide}
                                    >
                                        <NextIcon width={displayArrowSize} disabled={nextDisabled} />
                                    </button>
                                )
                            }
                            renderBottomCenterControls={() => null} // hides paginating dots
                            slidesToShow={slidesToShow}
                            slidesToScroll={slidesToScroll}
                            afterSlide={(slideIndex) => {
                                onSlideChange({
                                    name,
                                    slideIndex,
                                    slideNumber: Math.ceil(slideIndex / slidesToShow) + 1,
                                });
                                if (restoreScrollPosition) trackSlideChange(slideIndex);
                            }}
                            slideIndex={restoredSlideIndex}
                        >
                            {children}
                        </NukaCarousel>
                    </div>
                </div>
            </div>
        </div>
    );
}

Carousel.propTypes = {
    carousel: PropTypes.shape({
        description: PropTypes.string,
        featured: PropTypes.bool.isRequired,
        linkTitle: PropTypes.string,
        linkUrl: PropTypes.string,
        name: PropTypes.string,
        photoUrlLarge: PropTypes.string,
        photoUrlSmall: PropTypes.string,
    }).isRequired,
    children: PropTypes.node.isRequired,
    onSeeAllClick: PropTypes.func,
    onSlideChange: PropTypes.func,
    restoreScrollPosition: PropTypes.bool,
    scrollOverride: PropTypes.number,
    slideOverride: PropTypes.number,
};

Carousel.defaultProps = {
    onSeeAllClick: () => {},
    onSlideChange: () => {},
    restoreScrollPosition: false,
    scrollOverride: 0,
    slideOverride: 0,
};

export default Carousel;
